import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Backs one `_cc-gift-wrap-item` block that has its own `product` picked.
 *
 * When no product is picked, the block's checkbox is wired directly into the
 * closest product form via the native `form="..."` attribute (same trick as
 * stock `product-custom-property.liquid`), so it needs no JS at all - this
 * component only has work to do once a `data-variant-id` is present.
 *
 * @extends Component
 */
class GiftWrapItem extends Component {
  /** @type {HTMLFormElement | null} */
  #form = null;

  connectedCallback() {
    super.connectedCallback();
    if (!this.dataset.variantId) return;

    const formId = this.dataset.productFormId;
    this.#form = formId ? /** @type {HTMLFormElement | null} */ (document.getElementById(formId)) : null;
    this.#form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#form?.removeEventListener('submit', this.#onSubmit);
  }

  get #checkbox() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('.checkbox__input'));
  }

  /**
   * Fires (alongside the theme's own handler) when the closest Buy buttons form is submitted.
   * Runs independently of - not blocked by - the main product's own add-to-cart request, so both
   * lines are added in parallel from the same click.
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    if (!this.#checkbox?.checked) return;
    if (this.#form && !this.#form.checkValidity()) return;

    this.#addGiftWrapProduct();
  };

  async #addGiftWrapProduct() {
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    const attributeLabel = this.dataset.attributeLabel || 'Gift wrap for';
    const productTitle = this.dataset.productTitle ?? '';

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity: 1,
                properties: { [attributeLabel]: productTitle },
              },
            ],
          }),
        })
      );

      const result = await response.json();

      if (result.status) {
        console.warn('[cc-gift-wrap-item] Failed to add gift wrap product:', result.message);
        return;
      }

      const cartItemsComponent = document.querySelector('cart-items-component');
      if (cartItemsComponent) {
        await customElements.whenDefined('cart-items-component');
        await cartItemsComponent.fetchCartData();
      }

      // Lets other independent listeners (cart icon bubble, sticky add-to-cart, etc.) know a
      // line was added, same event the theme's own product form dispatches on success.
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity: 1 }],
          promise: Promise.resolve({ cart: null, detail: { source: 'cc-gift-wrap-item', didError: false } }),
        })
      );
    } catch (error) {
      console.warn('[cc-gift-wrap-item] Network error adding gift wrap product:', error);
    }
  }
}

if (!customElements.get('cc-gift-wrap-item')) {
  customElements.define('cc-gift-wrap-item', GiftWrapItem);
}
